import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { formatDate } from '../../../utils/formatters';

/**
 * The conference brief — what an author needs in front of them while filling in
 * the form.
 *
 * This column used to hold `SubmissionHistory`: a list of the papers you had
 * already submitted here. Wrong thing in the wrong place. You are on this
 * screen to submit a paper, and past submissions answer a question you are not
 * asking; `/my-submissions` is the screen whose whole job is that, and it does
 * it in far more detail.
 *
 * What belongs beside a form is the brief you are writing against. So the
 * column now carries the three things you keep looking up mid-sentence:
 * requirements, guidelines, and the dates.
 *
 * Requirements and guidelines are chips rather than the six-card grid this used
 * to be in the main column. That grid was ~600px of iconography restating one
 * line of text each; as chips the same content is scannable in a glance and
 * leaves the form the width it deserves.
 *
 * The dates are a definition list in mono, not chips — a date is a value you
 * read precisely, and mono numerals in a column line up so you can compare
 * them. They are also the only part of this panel that is genuinely per
 * conference rather than per platform.
 */

/** Only render a date row when the conference actually has that date. */
function DateRow({ label, value, tone = 'default' }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-border last:border-b-0">
      <dt className={`text-[12px] ${tone === 'urgent' ? 'text-error font-medium' : 'text-muted-foreground'}`}>
        {label}
      </dt>
      <dd
        className={`font-mono text-[12px] tabular-nums whitespace-nowrap ${
          tone === 'urgent' ? 'text-error font-medium' : 'text-foreground'
        }`}
        dir="ltr"
      >
        {formatDate(value)}
      </dd>
    </div>
  );
}

function Chip({ children, title }) {
  return (
    <span
      title={title}
      className="inline-flex items-center max-w-full px-2 py-1 bg-muted border border-border text-[12px] leading-snug text-foreground"
    >
      <span className="truncate">{children}</span>
    </span>
  );
}

function Group({ icon, title, children }) {
  return (
    <section className="p-4 border-b border-border last:border-b-0">
      <h3 className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2.5">
        <Icon name={icon} size={12} />
        {title}
      </h3>
      {children}
    </section>
  );
}

const ConferenceBrief = ({ conference }) => {
  const { t } = useTranslation('submissions');
  const k = (key, opts) => t(`authorPortal.requirements.${key}`, opts);

  // Reuses the existing requirement keys, so no translation work is lost — only
  // the presentation changed. `deadline` is deliberately absent: it is a date,
  // and it lives in the dates list below rather than being said twice.
  const requirements = ['format', 'authorLimit', 'pageLimit', 'reviewType', 'language'];
  const guidelines = ['original', 'anonymize', 'references', 'late'];

  const deadlinePassed =
    conference?.deadline && new Date(conference.deadline).getTime() < Date.now();

  return (
    <aside className="bg-card border border-border">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="font-heading font-bold text-foreground text-[19px] leading-[1.3]">
          {t('authorPortal.brief.title', 'Before you submit')}
        </h2>
        {conference?.name && (
          <p className="text-[12px] text-muted-foreground truncate">{conference.name}</p>
        )}
      </div>

      <Group icon="ClipboardList" title={t('authorPortal.brief.requirements', 'Requirements')}>
        <div className="flex flex-wrap gap-1.5">
          {requirements.map((key) => (
            <Chip key={key} title={k(`items.${key}.title`)}>
              {k(`items.${key}.description`)}
            </Chip>
          ))}
        </div>
      </Group>

      <Group icon="BookOpen" title={t('authorPortal.brief.guidelines', 'Guidelines')}>
        <div className="flex flex-wrap gap-1.5">
          {guidelines.map((key) => (
            <Chip key={key}>{k(`notes.${key}`)}</Chip>
          ))}
        </div>
      </Group>

      <Group icon="CalendarDays" title={t('authorPortal.brief.dates', 'Important dates')}>
        <dl>
          {/* The submission deadline is the one date that can be urgent, and it
              is the reason most people open this panel — so it leads, and it
              turns red once it has passed rather than reading as just another
              row you might have met. */}
          <DateRow
            label={t('authorPortal.brief.paperDeadline', 'Submission deadline')}
            value={conference?.deadline}
            tone={deadlinePassed ? 'urgent' : 'default'}
          />
          <DateRow
            label={t('authorPortal.brief.reviewDeadline', 'Reviews due')}
            value={conference?.reviewDeadline}
          />
          <DateRow
            label={t('authorPortal.brief.decisionDate', 'Decisions announced')}
            value={conference?.notificationDate}
          />
          <DateRow
            label={t('authorPortal.brief.cameraReady', 'Camera-ready due')}
            value={conference?.cameraReadyDeadline}
          />
          <DateRow
            label={t('authorPortal.brief.startDate', 'Conference starts')}
            value={conference?.startDate}
          />
          <DateRow
            label={t('authorPortal.brief.endDate', 'Conference ends')}
            value={conference?.endDate}
          />
        </dl>
        {/* A conference with no dates set at all would otherwise render an
            empty box with a heading, which reads as a loading failure. */}
        {!conference?.deadline &&
          !conference?.reviewDeadline &&
          !conference?.notificationDate &&
          !conference?.cameraReadyDeadline &&
          !conference?.startDate && (
            <p className="text-[12px] text-muted-foreground">
              {t('authorPortal.brief.noDates', 'The organisers have not published dates yet.')}
            </p>
          )}
      </Group>
    </aside>
  );
};

export default ConferenceBrief;
